// @flow

import React, { useState, useRef, forwardRef } from 'react';
import { log } from '../../utils/jsUtils';
import { FormControl } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import StripeInput from './StripeInput';

// Remove the old withStyles usage:
import combineStyles, { createClasses } from '../../utils/stylesUtils';
import signupStyles from '../PublicCpts/Styles/Signup.Style';
import stripeElementWrapperStyles from './Styles/StripeElementWrapper.Style';

// We also can use the MUI theme
import { useTheme } from '@mui/material/styles';

// Merge the style objects if you still want to combine them
const combinedStyles = combineStyles(signupStyles, stripeElementWrapperStyles);

type Props = {
  component: Function,
  label: string,
  id: string,
  // plus any other old props
};

type State = {
  focused: boolean,
  empty: boolean,
  error: boolean | Object,
  value: Object
};

const StripeElementWrapper = forwardRef<Props, HTMLDivElement>((props, ref) => {
  const [state, setState] = useState<State>({
    focused: false,
    empty: true,
    error: false,
    value: {}
  });

  const { component, label, id, ...restProps } = props;
  const { focused, empty, error } = state;
  
  // Create a separate elementRef for Stripe elements
  const elementRef = useRef(null);

  const handleBlur = () => {
    setState({ ...state, focused: false });
  };

  const handleFocus = () => {
    setState({ ...state, focused: true });
  };

  const handleChange = (changeObj) => {
    log('[StripeElementWrapper] handlechange:', changeObj);
    setState({
      ...state,
      error: changeObj.error,
      empty: changeObj.empty,
      value: {
        ...state.value,
        ...changeObj.value
      }
    });
  };

  // Build the classes object from merged styles
  const theme = useTheme();
  const classes = createClasses(combinedStyles, theme);

  log('[StripeEltWrapper] ref created: ', ref);

  return (
    <div ref={ref} {...restProps}>
      <FormControl
        sx={classes.newInputForm}
        style={{ marginTop: 8 }}
        fullWidth
        margin="normal"
      >
        <InputLabel
          error={!!error}
          focused={focused}
          shrink={focused || !empty}
        >
          {label}
        </InputLabel>

        {/**
         * We use MUI's `OutlinedInput` with `inputComponent={StripeInput}`,
         * replicating your old approach. The `inputProps={{ component }}` is
         * how we pass your custom `component` reference along to `StripeInput`.
         */}
        <OutlinedInput
          sx={classes.newFormElt}
          id={id}
          fullWidth
          inputComponent={StripeInput}
          inputProps={{ component }}
          // Don't pass the wrapper's ref to the inputRef prop
          // Instead create a new ref for the input
          inputRef={elementRef}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder=""
          variant="outlined"
        />
      </FormControl>

      {/*
        If you want to show the error text:
        {error && <div className={classes.errorLabel}>{error.message}</div>}
        (But the old code had it commented out, so we keep it commented for now.)
      */}
    </div>
  );
});

StripeElementWrapper.displayName = 'StripeElementWrapper';

// Use standard notation for Flow + ESLint compatibility
export default (StripeElementWrapper: React.ComponentType<Props>);